import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Card, Select, Spin, Empty } from "antd";
import {
  fetchProfileReportSummary,
  fetchProfileReportTimeseries,
} from "../../services/reports";
import UpgradeModal from "../BuySubscription/UpgradeModal";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const { Option } = Select;

const SUPPORTED_REPORT_PLATFORMS = [
  "FACEBOOK",
  "INSTAGRAM",
  "PINTEREST",
  "LINKEDIN_COMPANY",
  "YOUTUBE",
];

const getPlatformLabel = (platform) => {
  const labels = {
    FACEBOOK: "Facebook",
    INSTAGRAM: "Instagram",
    PINTEREST: "Pinterest",
    LINKEDIN_COMPANY: "LinkedIn Business",
    YOUTUBE: "YouTube",
  };

  return labels[platform] || platform;
};

const formatNumber = (value) => {
  const numericValue = Number(value || 0);
  return numericValue.toLocaleString();
};

const KpiCard = ({ title, value, subtitle = null }) => (
  <Card className="shadow-sm rounded-xl">
    <div className="text-sm text-gray-500">{title}</div>
    <div className="text-2xl font-bold text-gray-800 mt-2">{value ?? 0}</div>
    {subtitle ? <div className="text-xs text-gray-400 mt-1">{subtitle}</div> : null}
  </Card>
);

const ChartCard = ({
  title,
  data,
  dataKey,
  stroke = "#2563eb",
  yAxisWidth = 55,
}) => (
  <Card title={title} className="rounded-xl shadow-sm">
    <div style={{ width: "100%", height: 300 }}>
      {Array.isArray(data) && data.length > 0 ? (
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis width={yAxisWidth} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={stroke}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-full flex items-center justify-center">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data" />
        </div>
      )}
    </div>
  </Card>
);

const getDateRangeFromPreset = (preset) => {
  const today = new Date();
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);

  const start = new Date(today);

  if (preset === "7") {
    start.setDate(start.getDate() - 6);
  } else if (preset === "14") {
    start.setDate(start.getDate() - 13);
  } else if (preset === "28") {
    start.setDate(start.getDate() - 27);
  } else if (preset === "45") {
    start.setDate(start.getDate() - 44);
  } else if (preset === "60") {
    start.setDate(start.getDate() - 59);
  } else if (preset === "90") {
    start.setDate(start.getDate() - 89);
  } else {
    return {};
  }

  start.setHours(0, 0, 0, 0);

  return {
    since: start.toISOString(),
    until: end.toISOString(),
  };
};

export default function ProfileReports() {
  const { groups, selectedGroup, profile: availableProfiles } = useSelector(
(state) => state.groups
);
const { subscription } = useSelector((state) => state.subscription);
const reportsEnabled = subscription?.plan?.reports_enabled === true;
const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [datePreset, setDatePreset] = useState("28");
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
const [loading, setLoading] = useState(false);
const [upgradeModal, setUpgradeModal] = useState({
visible: false,
message: "",
featureName: "Reports",
});

  const selectedGroupObj = useMemo(
    () => groups.find((g) => g.name === selectedGroup),
    [groups, selectedGroup]
  );

  const platformOptions = useMemo(() => {
    const uniquePlatforms = [
      ...new Set((availableProfiles || []).map((p) => p.platform)),
    ];

    return uniquePlatforms.filter((platform) =>
      SUPPORTED_REPORT_PLATFORMS.includes(platform)
    );
  }, [availableProfiles]);

  useEffect(() => {
    if (platformOptions.length === 0) {
      setSelectedPlatform("");
      return;
    }

    if (!selectedPlatform || !platformOptions.includes(selectedPlatform)) {
      setSelectedPlatform(platformOptions[0]);
    }
  }, [platformOptions, selectedPlatform]);

  const filteredProfiles = useMemo(() => {
    return (availableProfiles || []).filter((p) => p.platform === selectedPlatform);
  }, [availableProfiles, selectedPlatform]);

  useEffect(() => {
    if (filteredProfiles.length > 0) {
      const profileIds = filteredProfiles.map(
        (profile) => profile.platform_pageId || profile.platform_id
      );

      if (!selectedProfileId || !profileIds.includes(selectedProfileId)) {
        setSelectedProfileId(profileIds[0]);
      }
    } else {
      setSelectedProfileId(null);
    }
  }, [filteredProfiles, selectedProfileId]);

  const isFacebook = selectedPlatform === "FACEBOOK";
  const isInstagram = selectedPlatform === "INSTAGRAM";
  const isPinterest = selectedPlatform === "PINTEREST";
  const isLinkedInCompany = selectedPlatform === "LINKEDIN_COMPANY";
  const isYouTube = selectedPlatform === "YOUTUBE";

  useEffect(() => {
    const loadReports = async () => {
if (subscription?.plan && !reportsEnabled) {
setSummary(null);
setTimeseries([]);
setUpgradeModal({
visible: true,
featureName: "Reports",
message: "Reports are not included in your current plan. Please upgrade to access Profile and Post Reports.",
});
return;
}

if (!selectedGroupObj?._id || !selectedProfileId || !selectedPlatform) {
setSummary(null);
setTimeseries([]);
return;
}
setLoading(true);
try {
        const { since, until } = getDateRangeFromPreset(datePreset);

        const [summaryRes, timeseriesRes] = await Promise.all([
          fetchProfileReportSummary({
            groupId: selectedGroupObj._id,
            platform: selectedPlatform,
            profileId: selectedProfileId,
            ...(since && { since }),
            ...(until && { until }),
          }),
          fetchProfileReportTimeseries({
            groupId: selectedGroupObj._id,
            platform: selectedPlatform,
            profileId: selectedProfileId,
            ...(since && { since }),
            ...(until && { until }),
          }),
        ]);

        setSummary(summaryRes.report || null);
        setTimeseries(timeseriesRes.reports || []);
      } catch (error) {
console.error(error);
setSummary(null);
setTimeseries([]);

if (error.isLimitError || error.errorType === "LIMIT_REACHED") {
setUpgradeModal({
visible: true,
featureName: error.feature || "Reports",
message: error.message,
});
}
} finally {
setLoading(false);
}
};
loadReports();
}, [selectedGroupObj, selectedProfileId, selectedPlatform, datePreset, reportsEnabled, subscription?.plan]);

  const instagramDailyRows = useMemo(
    () => (timeseries || []).filter((item) => item.rowType === "daily"),
    [timeseries]
  );

  const instagramBucketRows = useMemo(
    () => (timeseries || []).filter((item) => item.rowType === "bucket"),
    [timeseries]
  );

  const chartData = useMemo(
    () =>
      (timeseries || []).map((item) => ({
        date: new Date(item.reportDate).toLocaleDateString(),
        audience: item.metrics?.audience || 0,
        audience_growth: item.metrics?.audience_growth || 0,
        impressions: item.metrics?.impressions || 0,
        engagements: item.metrics?.engagements || 0,
        engaged_audience: item.metrics?.engaged_audience || 0,
        posts_count: item.metrics?.posts_count || 0,
        video_views: item.metrics?.video_views || 0,
        clicks: item.metrics?.clicks || 0,
        saves: item.metrics?.saves || 0,
        engagement_rate: item.metrics?.engagement_rate_per_impression || 0,
        reach: item.metrics?.reach || 0,
        page_views: item.metrics?.page_views || 0,
        unique_page_views: item.metrics?.unique_page_views || 0,
        monthly_views: item.metrics?.monthly_views || 0,
        })),
    [timeseries]
  );

  const instagramFollowersChartData = useMemo(
    () =>
      instagramDailyRows.map((item) => ({
        date: new Date(item.reportDate).toLocaleDateString(),
        audience_growth: item.metrics?.audience_growth || 0,
        audience: item.metrics?.audience || 0,
      })),
    [instagramDailyRows]
  );

  const instagramReachChartData = useMemo(
    () =>
      instagramDailyRows.map((item) => ({
        date: new Date(item.reportDate).toLocaleDateString(),
        reach: item.metrics?.reach || 0,
        posts_count: item.metrics?.posts_count || 0,
      })),
    [instagramDailyRows]
  );

  const instagramAggregateChartData = useMemo(
    () =>
      instagramBucketRows.map((item) => ({
        date: new Date(item.reportDate).toLocaleDateString(),
        impressions: item.metrics?.impressions || 0,
        engagements: item.metrics?.engagements || 0,
        engaged_audience: item.metrics?.engaged_audience || 0,
        clicks: item.metrics?.clicks || 0,
        engagement_rate: item.metrics?.engagement_rate_per_impression || 0,
        posts_count: item.metrics?.posts_count || 0,
        bucketStart: item.bucketStart || null,
        bucketEnd: item.bucketEnd || null,
        bucketSizeDays: item.bucketSizeDays || null,
      })),
    [instagramBucketRows]
  );

  const audienceLabel = isYouTube
    ? "Subscribers"
    : isLinkedInCompany
    ? "Followers"
    : "Audience";

  const growthLabel = isYouTube ? "Subscriber Growth" : "Audience Growth";
  const impressionsLabel = isInstagram ? "Views" : "Impressions";
  const clicksLabel = isPinterest ? "Pin / Outbound Clicks" : "Clicks";

  return (
    <>

    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Profile Reports</h1>
        <p className="text-gray-500">
          Profile-level analytics for connected social channels.
        </p>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="min-w-[220px]">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Platform
            </label>
            <Select
              value={selectedPlatform || undefined}
              onChange={setSelectedPlatform}
              style={{ width: "100%" }}
              placeholder="Select Platform"
            >
              {platformOptions.map((platform) => (
                <Option key={platform} value={platform}>
                  {getPlatformLabel(platform)}
                </Option>
              ))}
            </Select>
          </div>

          <div className="min-w-[260px]">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Profile
            </label>
            <Select
              value={selectedProfileId || undefined}
              onChange={setSelectedProfileId}
              style={{ width: "100%" }}
              placeholder="Select Profile"
            >
              {filteredProfiles.map((p, idx) => (
                <Option
                  key={`${p.platform}-${p.platform_pageId || p.platform_id}-${idx}`}
                  value={p.platform_pageId || p.platform_id}
                >
                  {p.profile}
                </Option>
              ))}
            </Select>
          </div>

          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Date Range
            </label>
            <Select
              value={datePreset}
              onChange={setDatePreset}
              style={{ width: "100%" }}
            >
              <Option value="7">Last 7 days</Option>
              <Option value="14">Last 14 days</Option>
              <Option value="28">Last 28 days</Option>
              <Option value="45">Last 45 days</Option>
              <Option value="60">Last 60 days</Option>
              <Option value="90">Last 90 days</Option>
              <Option value="max">Max</Option>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : !summary ? (
        <div className="bg-white rounded-xl border shadow-sm p-10">
          <Empty description="No profile report data found yet." />
        </div>
      ) : (
        <>
          <div
            className={`grid gap-4 mb-6 ${
              isInstagram
                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-7"
                : isPinterest
                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-7"
                : isLinkedInCompany
                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-5 2xl:grid-cols-10"
                : isYouTube
                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-5 2xl:grid-cols-7"
                : "grid-cols-1 md:grid-cols-2 xl:grid-cols-5 2xl:grid-cols-10"
            }`}
          >
            <KpiCard
              title={audienceLabel}
              value={formatNumber(summary.metrics?.audience)}
              subtitle={
                isInstagram || isPinterest || isLinkedInCompany || isYouTube
                  ? isYouTube
                    ? "Current subscribers"
                    : "Current followers"
                  : null
              }
            />

            {isInstagram ? (
              <>
                <KpiCard title="Reach" value={formatNumber(summary.metrics?.reach)} />
                <KpiCard
                  title="Engagements"
                  value={formatNumber(summary.metrics?.engagements)}
                />
                <KpiCard
                  title="Engagement Rate %"
                  value={summary.metrics?.engagement_rate_per_impression}
                />
                <KpiCard
                  title="Total Posts"
                  value={formatNumber(summary.metrics?.posts_count)}
                />
                <KpiCard
                  title="Accounts Engaged"
                  value={formatNumber(summary.metrics?.engaged_audience)}
                />
                <KpiCard
                  title="Clicks"
                  value={formatNumber(summary.metrics?.clicks)}
                  subtitle="Website + profile link taps"
                />
              </>
            ) : isPinterest ? (
<>
<KpiCard
title="Monthly Views"
value={formatNumber(summary.metrics?.monthly_views)}
subtitle="Pinterest monthly profile views"
/>
<KpiCard
title="Audience Growth"
value={formatNumber(summary.metrics?.audience_growth)}
/>
                <KpiCard
                  title="Impressions"
                  value={formatNumber(summary.metrics?.impressions)}
                />
                <KpiCard
                  title="Engagements"
                  value={formatNumber(summary.metrics?.engagements)}
                />
                <KpiCard
                  title={clicksLabel}
                  value={formatNumber(summary.metrics?.clicks)}
                />
                <KpiCard
                  title="Engagement Rate %"
                  value={summary.metrics?.engagement_rate_per_impression}
                />
                <KpiCard
                  title="Total Posts"
                  value={formatNumber(summary.metrics?.posts_count)}
                />
              </>
            ) : isLinkedInCompany ? (
              <>
                <KpiCard
                  title="Audience Growth"
                  value={formatNumber(summary.metrics?.audience_growth)}
                  subtitle="Organic + paid follower gains"
                />
                <KpiCard
                  title="Impressions"
                  value={formatNumber(summary.metrics?.impressions)}
                />
                <KpiCard
                  title="Engagements"
                  value={formatNumber(summary.metrics?.engagements)}
                />
                <KpiCard title="Clicks" value={formatNumber(summary.metrics?.clicks)} />
                <KpiCard title="Reach" value={formatNumber(summary.metrics?.reach)} />
                <KpiCard
                  title="Page Views"
                  value={formatNumber(summary.metrics?.page_views)}
                  subtitle="LinkedIn company page views"
                />
                <KpiCard
                  title="Unique Page Views"
                  value={formatNumber(summary.metrics?.unique_page_views)}
                  subtitle="Unique page visitors"
                />
                <KpiCard
                  title="Engagement Rate %"
                  value={summary.metrics?.engagement_rate_per_impression}
                />
                <KpiCard
                  title="Total Posts"
                  value={formatNumber(summary.metrics?.posts_count)}
                />
              </>
            ) : isYouTube ? (
              <>
                <KpiCard
                  title={growthLabel}
                  value={formatNumber(summary.metrics?.audience_growth)}
                />
                <KpiCard
                  title="Video Views"
                  value={formatNumber(summary.metrics?.video_views)}
                />
                <KpiCard
                  title="Engagements"
                  value={formatNumber(summary.metrics?.engagements)}
                />
                <KpiCard
                  title="Total Posts"
                  value={formatNumber(summary.metrics?.posts_count)}
                />
              </>
            ) : (
              <>
                <KpiCard
                title="Audience Growth"
                value={formatNumber(summary.metrics?.audience_growth)}
                />
                <KpiCard
                title={impressionsLabel}
                value={formatNumber(summary.metrics?.impressions)}
                />
                <KpiCard
                title="Engagements"
                value={formatNumber(summary.metrics?.engagements)}
                />
                <KpiCard
                title="Engagement Rate %"
                value={summary.metrics?.engagement_rate_per_impression}
                />
                <KpiCard
                title="Video Views"
                value={formatNumber(summary.metrics?.video_views)}
                />
                <KpiCard title="Reach" value={formatNumber(summary.metrics?.reach)} />
                <KpiCard
                title="Total Posts"
                value={formatNumber(summary.metrics?.posts_count)}
                />
                </>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <ChartCard
              title={audienceLabel}
              data={chartData}
              dataKey="audience"
              stroke="#2563eb"
            />

            {isInstagram ? (
              <>
                <ChartCard
                  title="Followers Growth (30 days max)"
                  data={instagramFollowersChartData}
                  dataKey="audience_growth"
                  stroke="#0f766e"
                />
                <ChartCard
                  title="Reach"
                  data={instagramReachChartData}
                  dataKey="reach"
                  stroke="#7c3aed"
                />
                <ChartCard
                  title="Views"
                  data={instagramAggregateChartData}
                  dataKey="impressions"
                  stroke="#6366f1"
                />
                <ChartCard
                  title="Engagements"
                  data={instagramAggregateChartData}
                  dataKey="engagements"
                  stroke="#16a34a"
                />
                <ChartCard
                  title="Accounts Engaged"
                  data={instagramAggregateChartData}
                  dataKey="engaged_audience"
                  stroke="#0891b2"
                />
                <ChartCard
                  title="Clicks"
                  data={instagramAggregateChartData}
                  dataKey="clicks"
                  stroke="#ca8a04"
                />
                <ChartCard
                  title="Engagement Rate %"
                  data={instagramAggregateChartData}
                  dataKey="engagement_rate"
                  stroke="#dc2626"
                />
              </>
            ) : isPinterest ? (
<>
<ChartCard
title="Monthly Views"
data={chartData}
dataKey="monthly_views"
stroke="#9333ea"
/>
<ChartCard
title="Impressions"
data={chartData}
dataKey="impressions"
stroke="#e60023"
/>
                <ChartCard
                  title="Engagements"
                  data={chartData}
                  dataKey="engagements"
                  stroke="#16a34a"
                />
                <ChartCard
                  title={clicksLabel}
                  data={chartData}
                  dataKey="clicks"
                  stroke="#ca8a04"
                />
                <ChartCard
                  title="Audience"
                  data={chartData}
                  dataKey="audience"
                  stroke="#2563eb"
                />
                <ChartCard
                  title="Audience Growth"
                  data={chartData}
                  dataKey="audience_growth"
                  stroke="#0f766e"
                />
                <ChartCard
title="Engagement Rate %"
data={chartData}
dataKey="engagement_rate"
stroke="#dc2626"
/>
<ChartCard
title="Total Posts"
data={chartData}
dataKey="posts_count"
stroke="#ea580c"
/>
</>
) : isLinkedInCompany ? (
              <>
                <ChartCard
                  title="Audience Growth"
                  data={chartData}
                  dataKey="audience_growth"
                  stroke="#0f766e"
                />
                <ChartCard
                  title="Impressions"
                  data={chartData}
                  dataKey="impressions"
                  stroke="#7c3aed"
                />
                <ChartCard
                  title="Engagements"
                  data={chartData}
                  dataKey="engagements"
                  stroke="#16a34a"
                />
                <ChartCard
                  title="Clicks"
                  data={chartData}
                  dataKey="clicks"
                  stroke="#ca8a04"
                />
                <ChartCard
                  title="Reach"
                  data={chartData}
                  dataKey="reach"
                  stroke="#6366f1"
                />
                <ChartCard
                  title="Page Views"
                  data={chartData}
                  dataKey="page_views"
                  stroke="#0ea5e9"
                />
                <ChartCard
                  title="Unique Page Views"
                  data={chartData}
                  dataKey="unique_page_views"
                  stroke="#14b8a6"
                />
                <ChartCard
                  title="Engagement Rate %"
                  data={chartData}
                  dataKey="engagement_rate"
                  stroke="#dc2626"
                />
                <ChartCard
                  title="Total Posts"
                  data={chartData}
                  dataKey="posts_count"
                  stroke="#ea580c"
                />
              </>
            ) : isYouTube ? (
              <>
                <ChartCard
                  title="Subscriber Growth"
                  data={chartData}
                  dataKey="audience_growth"
                  stroke="#0f766e"
                />
                <ChartCard
                  title="Engagements"
                  data={chartData}
                  dataKey="engagements"
                  stroke="#16a34a"
                />
                <ChartCard
                  title="Video Views"
                  data={chartData}
                  dataKey="video_views"
                  stroke="#be123c"
                />
                <ChartCard
                  title="Total Posts"
                  data={chartData}
                  dataKey="posts_count"
                  stroke="#ea580c"
                />
              </>
            ) : (
              <>
<ChartCard
title="Audience Growth"
data={chartData}
dataKey="audience_growth"
stroke="#0f766e"
/>
<ChartCard
title={impressionsLabel}
data={chartData}
dataKey="impressions"
stroke="#7c3aed"
/>
<ChartCard
title="Engagements"
data={chartData}
dataKey="engagements"
stroke="#16a34a"
/>
<ChartCard
title="Engagement Rate %"
data={chartData}
dataKey="engagement_rate"
stroke="#dc2626"
/>
<ChartCard
title="Total Posts"
data={chartData}
dataKey="posts_count"
stroke="#ea580c"
/>
<ChartCard
title="Video Views"
data={chartData}
dataKey="video_views"
stroke="#be123c"
/>
<ChartCard
title="Reach"
data={chartData}
dataKey="reach"
stroke="#6366f1"
/>
</>
            )}
          </div>
        </>
)}
</div>
<UpgradeModal
isOpen={upgradeModal.visible}
onClose={() =>
setUpgradeModal({
visible: false,
message: "",
featureName: "Reports",
})
}
message={upgradeModal.message}
featureName={upgradeModal.featureName}
/>
</>
);
}